/**
 * ObjectifsV2 — Page Objectifs V2 Esprit Padel OS
 * Cockpit SaaS premium : liste cockpit + vue détail 3 colonnes
 * READ-ONLY vis-à-vis de Firestore.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  useObjectives, useUsers, useClubs, useTasks, usePublications,
  useCalendarEvents, useCampagnes,
} from "../hooks/useV1Data";
import {
  defaultObjective, createObjective, updateObjective,
  duplicateObjective, archiveObjective,
  setItemObjective, createTaskForObjective,
} from "../hooks/useObjectivesWrite";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

/* Statut de la dernière synchro objectifs ↔ reporting (ep:objectives-sync-status). */
function useObjectivesSyncStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!db) return undefined;
    try {
      return onSnapshot(doc(db, "appdata", "ep:objectives-sync-status"), (snap) => {
        setStatus(snap.exists() ? snap.data().value : null);
      }, () => setStatus(null));
    } catch { return undefined; }
  }, []);
  return status;
}

/* ══════════════════════════════════════
   RÉFÉRENTIELS ÉDITION (admin)
══════════════════════════════════════ */
/* Réseaux sociaux proposés + option « hors réseaux » (point 6). */
const NETWORKS = ["Instagram", "Facebook", "TikTok", "LinkedIn", "Google Business", "YouTube", "Autre"];
/* Indicateurs connus → unité par défaut. metricKey réutilise les clés du sync Metricool. */
const METRICS = [
  { key: "nouveauxAbonnes", label: "Nouveaux abonnés", unit: "abonnés" },
  { key: "engagement",      label: "Taux d'engagement", unit: "%" },
  { key: "porteeMoyJour",   label: "Portée moyenne / jour", unit: "personnes / jour" },
  { key: "commentaires",    label: "Commentaires", unit: "commentaires" },
  { key: "partages",        label: "Partages", unit: "partages" },
  { key: "inscriptions",    label: "Inscriptions événement", unit: "inscriptions" },
  { key: "reservations",    label: "Réservations", unit: "réservations" },
  { key: "custom",          label: "Autre / personnalisé", unit: "" },
];
const PRIORITIES = [
  { key: "basse",   label: "Basse",   color: "#64748B" },
  { key: "normale", label: "Normale", color: "#0071CD" },
  { key: "haute",   label: "Haute",   color: "#D97706" },
  { key: "urgente", label: "Urgente", color: "#DC2626" },
];
const SOURCES = [
  { key: "manual",    label: "Saisie manuelle" },
  { key: "metricool", label: "Metricool" },
];

/* ══════════════════════════════════════
   HELPERS
══════════════════════════════════════ */
/*
 * objProgress — avancement RÉEL vers le résultat attendu, pas le ratio
 * valeur/cible. Mesure le chemin parcouru depuis la valeur de départ :
 *     hausse  : (actuelle − départ) / (cible − départ)
 *     baisse  : (départ − actuelle) / (départ − cible)
 * Gère le sens de l'objectif (direction, sinon déduit de cible vs départ)
 * et le dépassement (raw > 100). On ne se fie PLUS au champ `progress` figé
 * hérité de l'ancien environnement (qui valait valeur/cible) dès qu'une cible
 * exploitable est disponible — il reste un simple repli.
 * @returns {{pct:number, raw:number, done:boolean, over:boolean, computed:boolean}}
 */
function objProgress(o) {
  const c = Number(o.current || 0);
  const b = Number(o.baseline || 0);
  const t = Number(o.target || 0);
  const hasTarget = o.target !== undefined && o.target !== null && o.target !== "" && t !== 0;

  if (!hasTarget && b === t) {
    // Rien à calculer : repli sur le progress éventuellement stocké
    if (o.progress !== undefined && o.progress !== null) {
      const p = Math.max(0, Math.min(100, Math.round(Number(o.progress))));
      return { pct: p, raw: p, done: p >= 100, over: false, computed: false };
    }
    return { pct: 0, raw: 0, done: false, over: false, computed: false };
  }

  const decrease = o.direction === "decrease" || (o.direction == null && t < b);
  let raw;
  if (t === b) {
    raw = decrease ? (c <= t ? 100 : 0) : (c >= t ? 100 : 0);
  } else if (decrease) {
    raw = ((b - c) / (b - t)) * 100;
  } else {
    raw = ((c - b) / (t - b)) * 100;
  }
  raw = Math.round(raw);
  return {
    pct: Math.max(0, Math.min(100, raw)),
    raw,
    done: raw >= 100,
    over: raw > 100,
    computed: true,
  };
}

function objPct(o) {
  return objProgress(o).pct;
}
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}
function fmtShort(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}
function statusLabel(s) {
  const norm = (s || "").toLowerCase();
  if (norm === "active" || norm === "actif" || norm === "en cours") return "En cours";
  if (norm === "scheduled" || norm === "planifié") return "Planifié";
  if (norm === "success" || norm === "réussi") return "Réussi";
  if (norm === "almost_success") return "Presque réussi";
  if (norm === "failed" || norm === "raté") return "Raté";
  return s || "En cours";
}
function statusColor(s) {
  const n = (s || "").toLowerCase();
  if (n === "success" || n === "réussi") return { bg: "#eaf7ee", text: "#2f7a4f", dot: "#2f7a4f" };
  if (n === "almost_success") return { bg: "#fef8e4", text: "#8a5c00", dot: "#FFB600" };
  if (n === "failed" || n === "raté") return { bg: "#fdf0ee", text: "#B23B3B", dot: "#B23B3B" };
  if (n === "scheduled" || n === "planifié") return { bg: "#eff6ff", text: "#0071CD", dot: "#0071CD" };
  return { bg: "#eaf7ee", text: "#2f7a4f", dot: "#2f7a4f" };
}
function isActive(o) {
  const n = (o.status || "").toLowerCase();
  return !n || n === "active" || n === "actif" || n === "en cours";
}
function isScheduled(o) {
  const n = (o.status || "").toLowerCase();
  return n === "scheduled" || n === "planifié";
}
function isFinished(o) {
  const n = (o.status || "").toLowerCase();
  return n === "success" || n === "almost_success" || n === "failed" || n === "réussi" || n === "raté";
}

/* Résolution club — jamais d'ID visible */
function resolveClub(clubId, clubs) {
  if (!clubId) return null;
  if (clubs && clubs.length) {
    const c = clubs.find(x => String(x.id) === String(clubId));
    if (c) return c;
  }
  /* Fallback pour IDs historiques connus */
  const known = { "1": "Saint-Priest", "2": "La Boisse", "3": "Mâcon" };
  const name = known[String(clubId)];
  if (name) return { id: clubId, name, color: null };
  return { id: clubId, name: "Club non renseigné", color: null };
}
function clubAccentColor(club) {
  if (!club) return "#9896a0";
  if (club.color) return club.color;
  const map = { "Saint-Priest": "#0071CD", "La Boisse": "#FB8500", "Mâcon": "#2f7a4f" };
  return map[club.name] || "#9896a0";
}

function resolveUser(userId, users) {
  if (!userId || !users?.length) return null;
  return users.find(u => String(u.id) === String(userId) || String(u.appId) === String(userId)) || null;
}
function userName(user) {
  if (!user) return null;
  return user.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : user.name || null;
}
function initials(name) {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}
function daysLeft(deadline) {
  if (!deadline) return null;
  const d = new Date(deadline);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / 86400000);
}

/* Avancement attendu à la date du jour, d'après l'écoulement du temps. */
function expectedPct(o) {
  if (!o.startDate || !o.deadline) return null;
  const s = new Date(o.startDate), d = new Date(o.deadline), now = new Date();
  const total = d - s;
  if (!(total > 0)) return null;
  return Math.max(0, Math.min(100, ((now - s) / total) * 100));
}

/* État de santé d'un objectif pour la synthèse nationale (point 4). */
function objHealth(o) {
  if (isFinished(o)) {
    const n = (o.status || "").toLowerCase();
    return (n === "success" || n === "réussi") ? "done" : "risk";
  }
  const p = objPct(o);
  if (p >= 100) return "done";
  const dl = daysLeft(o.deadline);
  if (dl !== null && dl < 0) return "risk";
  const exp = expectedPct(o);
  if (exp == null) return p < 25 ? "behind" : "ontrack";
  if (p + 5 >= exp) return "ontrack";                       // tolérance 5 pts
  if (exp - p > 25 || (dl !== null && dl <= 10)) return "risk";
  return "behind";
}
const HEALTH_META = {
  done:    { label: "Atteints",            color: "#2f7a4f", bg: "#eaf7ee" },
  ontrack: { label: "Au rythme attendu",   color: "#0071CD", bg: "#eff6ff" },
  behind:  { label: "En retard",           color: "#b05a00", bg: "#fef8e4" },
  risk:    { label: "À intervenir",        color: "#bf3327", bg: "#fdf0ee" },
};

/* Calcule les signaux d'alerte à partir de données réelles */
function computeAlerts(o, linkedTasks) {
  const alerts = [];
  const dl = daysLeft(o.deadline);
  const p = objPct(o);
  if (dl !== null && dl < 0 && !isFinished(o)) {
    alerts.push({ type: "red", text: `Échéance dépassée de ${Math.abs(dl)}j` });
  } else if (dl !== null && dl <= 10 && dl >= 0 && !isFinished(o)) {
    alerts.push({ type: "orange", text: `Échéance dans ${dl} jour${dl > 1 ? "s" : ""}` });
  }
  if (!isFinished(o) && !isScheduled(o) && p < 25) {
    alerts.push({ type: "orange", text: "Progression inférieure à 25%" });
  }
  const overdueTasks = (linkedTasks || []).filter(t => {
    if (!t || t.status === "Terminé" || t.status === "done") return false;
    return t.deadline && new Date(t.deadline) < new Date();
  });
  if (overdueTasks.length > 0) {
    alerts.push({ type: "orange", text: `${overdueTasks.length} tâche${overdueTasks.length > 1 ? "s" : ""} en retard` });
  }
  return alerts;
}

/* ══════════════════════════════════════
   SVG ICONS
══════════════════════════════════════ */
function IcoTarget({ size = 14, ...p }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /></svg>;
}
function IcoChevR({ size = 12 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9,18 15,12 9,6" /></svg>;
}
function IcoChevL({ size = 13 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15,18 9,12 15,6" /></svg>;
}
function IcoCal({ size = 12 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></svg>;
}
function IcoUser({ size = 12 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
}
function IcoAlert({ size = 12 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
}
function IcoEdit({ size = 13 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>;
}
function IcoCheck({ size = 10 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20,6 9,17 4,12" /></svg>;
}
function IcoPlus({ size = 11 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
}
function IcoRefresh({ size = 12, spinning = false }) {
  return <svg className={spinning ? "obj-spin" : undefined} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 11-2.64-6.36" /><polyline points="21 3 21 9 15 9" /></svg>;
}
function IcoMega({ size = 13 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a4 4 0 010 8" /><path d="M20 4a8 8 0 010 16" /><rect x="2" y="9" width="8" height="6" rx="1.5" /><path d="M10 9l8-5v16l-8-5" /></svg>;
}
function IcoLayers({ size = 13 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12,2 22,8.5 12,15 2,8.5" /><path d="M2 15.5l10 6.5 10-6.5" /><path d="M2 11.5l10 6.5 10-6.5" /></svg>;
}
function IcoClock({ size = 12 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" /></svg>;
}
function IcoEvent({ size = 13 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /><path d="M8 14h.01M12 14h.01M16 14h.01" /></svg>;
}
function IcoBar({ size = 13 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="14" width="4" height="7" rx="1" /><rect x="10" y="9" width="4" height="12" rx="1" /><rect x="17" y="5" width="4" height="16" rx="1" /></svg>;
}

/* ── Primitives ── */
function Badge({ label, bg, color, dot }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: bg || "var(--gray-bg)", color: color || "var(--text-2)",
      fontSize: 10.5, fontWeight: 700, borderRadius: 20,
      padding: "2px 9px", lineHeight: 1.6, whiteSpace: "nowrap",
    }}>
      {dot && <span style={{ width: 5, height: 5, borderRadius: "50%", background: dot, display: "inline-block" }} />}
      {label}
    </span>
  );
}
function ProgressBar({ value, color, height = 4 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const c = color || (v >= 80 ? "#2f7a4f" : v >= 50 ? "#FFB600" : "#FB8500");
  return (
    <div style={{ height, borderRadius: height, background: "#ECEAE5", overflow: "hidden", minWidth: 0 }}>
      <div style={{ height: "100%", width: `${v}%`, background: c, borderRadius: height, transition: "width .35s ease" }} />
    </div>
  );
}
function Avatar({ name, size = 26 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "#e0eaff", color: "#0071CD",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.36, fontWeight: 700, flexShrink: 0,
    }}>
      {initials(name)}
    </div>
  );
}
function EmptyRow({ icon, text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", color: "var(--text-3)", fontSize: 12 }}>
      <span style={{ opacity: .5 }}>{icon}</span>
      <span>{text}</span>
    </div>
  );
}

/* ══════════════════════════════════════
   LISTE — MODULE COCKPIT GAUCHE
══════════════════════════════════════ */
function WatchModule({ objectives, clubs, tasks }) {
  /* Objectifs nécessitant attention (règles calculées, pas de priorité inventée) */
  const watched = useMemo(() => {
    return objectives
      .filter(o => !isFinished(o) && !isScheduled(o))
      .map(o => {
        const linkedT = tasks.filter(t => String(t.objectiveId) === String(o.id) || String(t.objective) === String(o.id));
        const alerts = computeAlerts(o, linkedT);
        return { o, alerts };
      })
      .filter(x => x.alerts.length > 0)
      .slice(0, 4);
  }, [objectives, tasks]);

  if (watched.length === 0) return null;
  return (
    <div className="obj-module">
      <div className="obj-module-header">
        <span style={{ color: "#8a5c00" }}><IcoAlert size={12} /></span>
        <span className="obj-module-title">À surveiller</span>
        <span className="obj-module-count" style={{ background: "var(--orange-bg)", color: "#8a5c00" }}>{watched.length}</span>
      </div>
      {watched.map(({ o, alerts }) => {
        const club = resolveClub(o.club, clubs);
        const acc = clubAccentColor(club);
        const p = objPct(o);
        return (
          <div key={o.id} className="obj-module-row">
            <div className="obj-module-row-dot" style={{ background: acc }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="obj-module-row-name">{o.title}</div>
              <div className="obj-module-row-meta">{alerts[0]?.text}</div>
              <ProgressBar value={p} height={3} />
            </div>
            <span className="obj-module-row-pct">{p}%</span>
          </div>
        );
      })}
    </div>
  );
}

function DeadlineModule({ objectives }) {
  const upcoming = useMemo(() => {
    return objectives
      .filter(o => !isFinished(o) && o.deadline)
      .map(o => ({ o, dl: daysLeft(o.deadline) }))
      .filter(x => x.dl !== null && x.dl >= 0 && x.dl <= 60)
      .sort((a, b) => a.dl - b.dl)
      .slice(0, 5);
  }, [objectives]);

  if (upcoming.length === 0) return null;
  return (
    <div className="obj-module">
      <div className="obj-module-header">
        <span style={{ color: "var(--ep-blue)" }}><IcoCal size={12} /></span>
        <span className="obj-module-title">Prochaines échéances</span>
      </div>
      {upcoming.map(({ o, dl }) => {
        const isClose = dl <= 14;
        return (
          <div key={o.id} className="obj-module-row">
            <div className="obj-deadline-badge" style={{ color: isClose ? "#8a5c00" : "var(--ep-blue)", background: isClose ? "var(--orange-bg)" : "var(--blue-bg)" }}>
              <span style={{ fontSize: 13, fontWeight: 800, lineHeight: 1 }}>{dl}</span>
              <span style={{ fontSize: 9, fontWeight: 600 }}>j</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="obj-module-row-name">{o.title}</div>
              <div className="obj-module-row-meta">{fmtDate(o.deadline)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SummaryModule({ objectives, scopeLabel }) {
  const actifs = objectives.filter(o => isActive(o)).length;
  const planifies = objectives.filter(o => isScheduled(o)).length;
  const termines = objectives.filter(o => isFinished(o)).length;
  const avgPct = useMemo(() => {
    const active = objectives.filter(o => isActive(o));
    if (!active.length) return 0;
    return Math.round(active.reduce((acc, o) => acc + objPct(o), 0) / active.length);
  }, [objectives]);
  const health = useMemo(() => {
    const acc = { done: 0, ontrack: 0, behind: 0, risk: 0 };
    objectives.forEach(o => { acc[objHealth(o)] += 1; });
    return acc;
  }, [objectives]);

  return (
    <div className="obj-module obj-module--summary">
      <div className="obj-module-header">
        <span style={{ color: "#8a5c00" }}><IcoBar size={12} /></span>
        <span className="obj-module-title">{scopeLabel || "Vue d'ensemble"}</span>
      </div>
      <div className="obj-summary-stats">
        <div className="obj-summary-stat">
          <span className="obj-summary-val" style={{ color: "#2f7a4f" }}>{actifs}</span>
          <span className="obj-summary-label">En cours</span>
        </div>
        <div className="obj-summary-stat">
          <span className="obj-summary-val" style={{ color: "var(--ep-blue)" }}>{planifies}</span>
          <span className="obj-summary-label">Planifiés</span>
        </div>
        <div className="obj-summary-stat">
          <span className="obj-summary-val" style={{ color: "var(--text-3)" }}>{termines}</span>
          <span className="obj-summary-label">Terminés</span>
        </div>
      </div>
      {actifs > 0 && (
        <div style={{ padding: "0 14px 12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-3)", marginBottom: 5 }}>
            <span>Progression moyenne</span>
            <span style={{ fontWeight: 700, color: "var(--text)" }}>{avgPct}%</span>
          </div>
          <ProgressBar value={avgPct} height={5} />
        </div>
      )}
      {/* Synthèse : état d'avancement (point 4) */}
      <div className="obj-health-grid">
        {["done", "ontrack", "behind", "risk"].map(k => {
          const m = HEALTH_META[k];
          return (
            <div key={k} className="obj-health-cell" style={{ background: m.bg }}>
              <span className="obj-health-val" style={{ color: m.color }}>{health[k]}</span>
              <span className="obj-health-label">{m.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   LISTE — TABLEAU COMPACT DROITE
══════════════════════════════════════ */
function ObjTableRow({ o, clubs, users, onSelect }) {
  const club = resolveClub(o.club, clubs);
  const acc = clubAccentColor(club);
  const p = objPct(o);
  const sc = statusColor(o.status);
  const dl = daysLeft(o.deadline);
  const owner = resolveUser(o.owner || (o.assignedTo && o.assignedTo[0]), users);
  const ownerName = userName(owner);
  const dlText = dl === null ? null : dl < 0 ? `${Math.abs(dl)}j retard` : dl === 0 ? "Auj." : `${dl}j`;
  const dlColor = dl !== null && dl < 0 ? "var(--red)" : dl !== null && dl <= 10 ? "#8a5c00" : "var(--text-3)";

  return (
    <div className="obj-table-row" onClick={() => onSelect(o)}>
      <div className="obj-table-row-indicator" style={{ background: acc }} />
      <div className="obj-table-col obj-table-col--main">
        <div className="obj-table-name">{o.title}</div>
        <div className="obj-table-club" style={{ color: acc }}>{club?.name}</div>
      </div>
      <div className="obj-table-col obj-table-col--progress">
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
          <span className="obj-table-pct">{p}%</span>
          <div style={{ flex: 1 }}><ProgressBar value={p} height={4} /></div>
        </div>
        <div style={{ fontSize: 10.5, color: "var(--text-3)", fontVariantNumeric: "tabular-nums" }}>
          {Number(o.current || 0).toLocaleString("fr-FR")} / {Number(o.target || 0).toLocaleString("fr-FR")} {o.unit}
        </div>
      </div>
      <div className="obj-table-col obj-table-col--status">
        <Badge label={statusLabel(o.status)} bg={sc.bg} color={sc.text} dot={sc.dot} />
      </div>
      <div className="obj-table-col obj-table-col--deadline">
        <span style={{ color: dlColor, fontSize: 11.5, fontWeight: 600 }}>{dlText || "—"}</span>
        <div style={{ fontSize: 10.5, color: "var(--text-3)", marginTop: 1 }}>{fmtShort(o.deadline)}</div>
      </div>
      <div className="obj-table-col obj-table-col--owner">
        {ownerName ? (
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Avatar name={ownerName} size={22} />
            <span style={{ fontSize: 11, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 80 }}>{ownerName}</span>
          </div>
        ) : <span style={{ color: "var(--text-3)", fontSize: 11 }}>—</span>}
      </div>
      <div className="obj-table-col obj-table-col--arrow">
        <IcoChevR size={11} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   PAGE LISTE PRINCIPALE
══════════════════════════════════════ */
/* Ids clubs normalisés d'un objectif (clubs[] ou club legacy). */
function objClubIds(o) {
  if (Array.isArray(o.clubs) && o.clubs.length) return o.clubs.map(String);
  return o.club != null ? [String(o.club)] : [];
}

function fmtSyncWhen(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) + " à " +
    d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function ObjectifsList({ objectives, archived = [], clubs, users, tasks, isAdmin, syncStatus, refreshing, onRefresh, onSelect, onCreate }) {
  const [filter, setFilter] = useState("current");
  const [fClub, setFClub]       = useState("");
  const [fOwner, setFOwner]     = useState("");
  const [fNetwork, setFNetwork] = useState("");
  const [fMetric, setFMetric]   = useState("");
  const [fPeriod, setFPeriod]   = useState("all");

  const allForOpts = useMemo(() => [...objectives, ...archived], [objectives, archived]);

  /* Options de filtres, dérivées des données réelles */
  const ownerOpts = useMemo(() => {
    const ids = new Set();
    allForOpts.forEach(o => {
      if (o.owner != null) ids.add(String(o.owner));
      (o.assignedTo || []).forEach(a => a != null && ids.add(String(a)));
    });
    return Array.from(ids)
      .map(id => ({ id, name: userName(resolveUser(id, users)) }))
      .filter(x => x.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allForOpts, users]);
  const networkOpts = useMemo(
    () => Array.from(new Set(allForOpts.map(o => o.network).filter(Boolean))).sort(),
    [allForOpts]
  );
  const metricOpts = useMemo(
    () => Array.from(new Set(allForOpts.map(o => o.metricLabel || o.metricKey).filter(Boolean))).sort(),
    [allForOpts]
  );

  const inPeriod = (o) => {
    if (fPeriod === "all") return true;
    if (!o.deadline) return false;
    const d = new Date(o.deadline);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    if (d.getFullYear() !== now.getFullYear()) return false;
    if (fPeriod === "year") return true;
    if (fPeriod === "month") return d.getMonth() === now.getMonth();
    if (fPeriod === "quarter") return Math.floor(d.getMonth() / 3) === Math.floor(now.getMonth() / 3);
    return true;
  };
  const matchScope = (o) => {
    if (fClub && !objClubIds(o).includes(fClub)) return false;
    if (fOwner) {
      const ids = [o.owner, ...(o.assignedTo || [])].filter(x => x != null).map(String);
      if (!ids.includes(fOwner)) return false;
    }
    if (fNetwork && String(o.network || "") !== fNetwork) return false;
    if (fMetric && String(o.metricLabel || o.metricKey || "") !== fMetric) return false;
    if (!inPeriod(o)) return false;
    return true;
  };

  const scoped         = useMemo(() => objectives.filter(matchScope), [objectives, fClub, fOwner, fNetwork, fMetric, fPeriod]);
  const scopedArchived = useMemo(() => archived.filter(matchScope),   [archived, fClub, fOwner, fNetwork, fMetric, fPeriod]);
  const hasFilter = fClub || fOwner || fNetwork || fMetric || fPeriod !== "all";

  const FILTERS = [
    { key: "current", label: "En cours" },
    { key: "scheduled", label: "Planifiés" },
    { key: "finished", label: "Terminés" },
    { key: "all", label: "Tous" },
    ...(isAdmin && scopedArchived.length ? [{ key: "archived", label: "Archivés" }] : []),
  ];
  const filtered = useMemo(() => {
    if (filter === "archived") return scopedArchived;
    return scoped.filter(o =>
      filter === "all" ? true :
      filter === "current" ? isActive(o) :
      filter === "scheduled" ? isScheduled(o) :
      filter === "finished" ? isFinished(o) :
      true
    );
  }, [scoped, scopedArchived, filter]);

  const alertCount = useMemo(() => scoped.filter(o => {
    if (isFinished(o)) return false;
    const dl = daysLeft(o.deadline);
    const p = objPct(o);
    return (dl !== null && dl < 0) || (dl !== null && dl <= 10) || p < 25;
  }).length, [scoped]);

  const scopeLabel = fClub ? (resolveClub(fClub, clubs)?.name || "Club") : "Vue France";

  return (
    <div className="obj-list-page">
      {/* Header */}
      <div className="obj-list-header">
        <div className="obj-list-header-left">
          <div className="obj-list-icon"><IcoTarget size={18} /></div>
          <div>
            <p className="obj-list-sub">
              <span className="obj-scope-chip">{scopeLabel}</span>
              {scoped.filter(isActive).length} en cours
              {alertCount > 0 && <span className="obj-alert-pill"><IcoAlert size={10} /> {alertCount} à surveiller</span>}
            </p>
          </div>
        </div>
        <div className="obj-header-right">
          <div className="obj-filter-tabs">
            {FILTERS.map(f => (
              <button key={f.key} className={`obj-filter-tab${filter === f.key ? " active" : ""}`} onClick={() => setFilter(f.key)}>
                {f.label}
                <span className="obj-filter-tab-count">
                  {f.key === "current" ? scoped.filter(isActive).length :
                   f.key === "scheduled" ? scoped.filter(isScheduled).length :
                   f.key === "finished" ? scoped.filter(isFinished).length :
                   f.key === "archived" ? scopedArchived.length :
                   scoped.length}
                </span>
              </button>
            ))}
          </div>
          <div className="obj-sync">
            {syncStatus && syncStatus.ok === false ? (
              <span className="obj-sync-when obj-sync-when--err" title={syncStatus.error || ""}>
                <IcoAlert size={11} /> Échec synchro
              </span>
            ) : fmtSyncWhen(syncStatus && syncStatus.syncedAt) ? (
              <span className="obj-sync-when">Synchro&nbsp;: {fmtSyncWhen(syncStatus.syncedAt)}</span>
            ) : null}
            {isAdmin && (
              <button className="obj-sync-btn" onClick={onRefresh} disabled={refreshing} title="Actualiser les valeurs depuis le reporting">
                <IcoRefresh size={12} spinning={refreshing} /> {refreshing ? "Actualisation…" : "Actualiser"}
              </button>
            )}
          </div>
          {isAdmin && (
            <button className="camp-btn camp-btn--primary obj-create-btn" onClick={onCreate}>
              <IcoPlus size={12} /> Créer un objectif
            </button>
          )}
        </div>
      </div>

      {/* Barre de filtres — vue France / par club (point 4) */}
      <div className="obj-filter-bar">
        <select className="obj-filter-select" value={fClub} onChange={e => setFClub(e.target.value)}>
          <option value="">Tous les clubs (France)</option>
          {(clubs || []).map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
        <select className="obj-filter-select" value={fOwner} onChange={e => setFOwner(e.target.value)}>
          <option value="">Tous les responsables</option>
          {ownerOpts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {networkOpts.length > 0 && (
          <select className="obj-filter-select" value={fNetwork} onChange={e => setFNetwork(e.target.value)}>
            <option value="">Tous les réseaux</option>
            {networkOpts.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
        {metricOpts.length > 0 && (
          <select className="obj-filter-select" value={fMetric} onChange={e => setFMetric(e.target.value)}>
            <option value="">Tous les indicateurs</option>
            {metricOpts.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        <select className="obj-filter-select" value={fPeriod} onChange={e => setFPeriod(e.target.value)}>
          <option value="all">Toutes périodes</option>
          <option value="month">Ce mois</option>
          <option value="quarter">Ce trimestre</option>
          <option value="year">Cette année</option>
        </select>
        {hasFilter && (
          <button className="obj-filter-clear" onClick={() => { setFClub(""); setFOwner(""); setFNetwork(""); setFMetric(""); setFPeriod("all"); }}>
            Réinitialiser
          </button>
        )}
      </div>

      {/* Cockpit layout */}
      <div className="obj-cockpit">
        {/* Colonne gauche — modules */}
        <div className="obj-cockpit-left">
          <SummaryModule objectives={scoped} scopeLabel={scopeLabel} />
          <WatchModule objectives={scoped} clubs={clubs} tasks={tasks} />
          <DeadlineModule objectives={scoped} />
        </div>

        {/* Colonne droite — tableau */}
        <div className="obj-cockpit-right">
          {filtered.length === 0 ? (
            <div className="obj-table-empty">
              <IcoTarget size={26} />
              <span>Aucun objectif pour ce filtre</span>
            </div>
          ) : (
            <>
              <div className="obj-table-head">
                <div className="obj-table-col obj-table-col--main">Objectif</div>
                <div className="obj-table-col obj-table-col--progress">Progression</div>
                <div className="obj-table-col obj-table-col--status">Statut</div>
                <div className="obj-table-col obj-table-col--deadline">Échéance</div>
                <div className="obj-table-col obj-table-col--owner">Responsable</div>
                <div className="obj-table-col obj-table-col--arrow" />
              </div>
              {filtered.map(o => (
                <ObjTableRow key={o.id} o={o} clubs={clubs} users={users} onSelect={onSelect} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   DÉTAIL — COLONNE GAUCHE
══════════════════════════════════════ */
function DetailLeft({ obj, clubs, users }) {
  const club = resolveClub(obj.club, clubs);
  const acc = clubAccentColor(club);
  const sc = statusColor(obj.status);
  const owner = resolveUser(obj.owner, users);
  const ownerName = userName(owner);

  return (
    <>
      {/* Bloc identité */}
      <div className="obj-block">
        <div className="obj-block-header">Identité de l'objectif</div>
        <div className="obj-block-body">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: `${acc}18`, color: acc, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <IcoTarget size={20} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3, color: "var(--text)" }}>{obj.title}</div>
              <div style={{ marginTop: 5, display: "flex", gap: 5, flexWrap: "wrap" }}>
                <Badge label={statusLabel(obj.status)} bg={sc.bg} color={sc.text} dot={sc.dot} />
              </div>
            </div>
          </div>

          {[
            { icon: <IcoCal />, label: "Début", val: fmtDate(obj.startDate) },
            { icon: <IcoCal />, label: "Échéance", val: fmtDate(obj.deadline) },
            { icon: <IcoUser />, label: "Responsable", val: ownerName || "—" },
            { icon: <span style={{ fontSize: 11 }}>◈</span>, label: "Club", val: (
              <span style={{ fontSize: 11, fontWeight: 700, color: acc, background: `${acc}15`, padding: "2px 9px", borderRadius: 20, border: `1px solid ${acc}30` }}>
                {club?.name}
              </span>
            )},
            { icon: <IcoBar />, label: "Source", val: obj.source || obj.metricKey || "—" },
            { icon: <IcoClock />, label: "Créé le", val: fmtDate(obj.createdAt) },
          ].map((r, i) => (
            <div key={i} className="obj-info-row">
              <span className="obj-info-icon">{r.icon}</span>
              <span className="obj-info-label">{r.label}</span>
              <span className="obj-info-val">{r.val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Résultat attendu */}
      <div className="obj-block obj-block--accent">
        <div className="obj-block-header">Résultat attendu</div>
        <div className="obj-block-body">
          {obj.target ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>
                {Number(obj.target).toLocaleString("fr-FR")}
              </span>
              <span style={{ fontSize: 13, color: "var(--text-2)", fontWeight: 600 }}>{obj.unit}</span>
            </div>
          ) : null}
          <p style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.55, marginBottom: 8 }}>
            {obj.description || `Atteindre ${obj.target ? Number(obj.target).toLocaleString("fr-FR") : "la cible"} ${obj.unit || ""} d'ici le ${fmtDate(obj.deadline)}.`}
          </p>
          <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--text-3)" }}>
            <span>Départ&nbsp;: <strong style={{ color: "var(--text-2)" }}>{Number(obj.baseline || obj.current || 0).toLocaleString("fr-FR")} {obj.unit}</strong></span>
            {obj.target && <span>Cible&nbsp;: <strong style={{ color: "var(--text-2)" }}>{Number(obj.target).toLocaleString("fr-FR")} {obj.unit}</strong></span>}
          </div>
        </div>
      </div>
    </>
  );
}

/* ══════════════════════════════════════
   DÉTAIL — COLONNE CENTRALE
══════════════════════════════════════ */
function DetailCenter({ obj, users, tasks }) {
  const prog = objProgress(obj);
  const p = prog.pct;
  const dl = daysLeft(obj.deadline);
  const pColor = prog.over ? "#0071CD" : p >= 80 ? "#2f7a4f" : p >= 50 ? "#FFB600" : "#FB8500";
  const R = 30, C = 38, circ = 2 * Math.PI * R;
  const hasBaseline = obj.baseline !== undefined && obj.baseline !== null && obj.baseline !== "";

  /* Seuls les KPI réellement calculables */
  const linkedTasks = useMemo(() =>
    tasks.filter(t => t && (String(t.objectiveId) === String(obj.id) || String(t.objective) === String(obj.id))),
    [tasks, obj.id]
  );
  const doneTasks = linkedTasks.filter(t => t.status === "Terminé" || t.status === "done").length;
  const totalTasks = linkedTasks.length;

  const assignedUsers = useMemo(() => {
    if (!users?.length) return [];
    const ids = obj.assignedTo || (obj.owner ? [obj.owner] : []);
    return ids.map(id => resolveUser(id, users)).filter(Boolean);
  }, [users, obj]);

  return (
    <>
      {/* Progression */}
      <div className="obj-block">
        <div className="obj-block-header">Progression</div>
        <div className="obj-block-body" style={{ paddingBottom: 0 }}>
          {/* Compact ring + chiffre */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, paddingBottom: 14, borderBottom: "1px solid var(--border)" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width={C * 2} height={C * 2} viewBox={`0 0 ${C * 2} ${C * 2}`}>
                <circle cx={C} cy={C} r={R} fill="none" stroke="#ECEAE5" strokeWidth="6" />
                <circle cx={C} cy={C} r={R} fill="none"
                  stroke={pColor} strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={circ} strokeDashoffset={circ * (1 - p / 100)}
                  transform={`rotate(-90 ${C} ${C})`}
                />
              </svg>
              <span style={{ position: "absolute", fontSize: 16, fontWeight: 800, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{p}%</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 3, display: "flex", alignItems: "center", gap: 6 }}>
                Avancement global
                {prog.over && <Badge label={`Dépassé · ${prog.raw}%`} bg="var(--blue-bg)" color="#0071CD" />}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 4, fontVariantNumeric: "tabular-nums" }}>
                <strong>{Number(obj.current || 0).toLocaleString("fr-FR")}</strong>
                <span style={{ color: "var(--text-3)" }}> / {Number(obj.target || 0).toLocaleString("fr-FR")} {obj.unit}</span>
              </div>
              {prog.computed && hasBaseline && (
                <div style={{ fontSize: 10.5, color: "var(--text-3)", marginBottom: 6 }}>
                  Depuis le départ ({Number(obj.baseline || 0).toLocaleString("fr-FR")} → {Number(obj.target || 0).toLocaleString("fr-FR")} {obj.unit})
                </div>
              )}
              {(() => {
                const synced = obj.source === "metricool" && obj.syncStatus === "ok";
                return (
                  <div style={{ fontSize: 10.5, marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 4, color: synced ? "#0F56B8" : "var(--text-3)" }}>
                    {synced
                      ? <><IcoRefresh size={10} /> Synchronisé le {fmtDate(obj.updatedFromMetricoolAt)}</>
                      : <>Saisie manuelle</>}
                  </div>
                );
              })()}
              {dl !== null && (
                <div style={{ fontSize: 11, color: dl < 0 ? "var(--red)" : dl <= 14 ? "#8a5c00" : "var(--text-3)", display: "flex", alignItems: "center", gap: 4 }}>
                  <IcoClock size={11} />
                  {dl < 0 ? `${Math.abs(dl)}j de retard` : dl === 0 ? "Échéance aujourd'hui" : `${dl}j restants`}
                </div>
              )}
            </div>
          </div>

          {/* Indicateurs réels seulement */}
          <div className="obj-kpi-section">
            {/* Progression toujours présente */}
            <KpiLine icon={<IcoBar size={13} />} label="Progression" value={`${p}%`} progress={p} color={pColor} />
            {/* Tâches seulement si des relations existent */}
            {totalTasks > 0 && (
              <KpiLine icon={<IcoCheck size={12} />} label="Tâches liées" value={`${doneTasks} / ${totalTasks}`} progress={totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0} />
            )}
          </div>
        </div>
      </div>

      {/* Évolution */}
      <div className="obj-block">
        <div className="obj-block-header">Évolution</div>
        <div style={{ padding: "20px 16px", textAlign: "center" }}>
          <div style={{ color: "var(--text-3)", opacity: .4, marginBottom: 8 }}><IcoBar size={22} /></div>
          <p style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.5, maxWidth: 240, margin: "0 auto" }}>
            Le graphique d'évolution apparaîtra dès que plusieurs points de progression auront été enregistrés.
          </p>
        </div>
      </div>

      {/* Équipe */}
      <div className="obj-block">
        <div className="obj-block-header">Équipe</div>
        {assignedUsers.length === 0 ? (
          <EmptyRow icon={<IcoUser size={16} />} text="Aucun membre assigné" />
        ) : (
          <div>
            {assignedUsers.map((u, i) => {
              const name = userName(u) || "Membre";
              const isOwner = String(u.id) === String(obj.owner) || String(u.appId) === String(obj.owner);
              return (
                <div key={u.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderBottom: "1px solid var(--border)" }}>
                  <Avatar name={name} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text)" }}>{name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>{u.role || "Contributeur"}</div>
                  </div>
                  {isOwner && <Badge label="Responsable" bg="var(--yellow-bg)" color="#8a5c00" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function KpiLine({ icon, label, value, progress, color }) {
  const c = color || (progress >= 80 ? "#2f7a4f" : progress >= 50 ? "#FFB600" : "#FB8500");
  return (
    <div className="obj-kpi-line">
      <span className="obj-kpi-line-icon" style={{ color: c }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{value}</span>
        </div>
        <ProgressBar value={progress} color={c} height={3} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   DÉTAIL — COLONNE DROITE
══════════════════════════════════════ */
function RelatedBlock({ title, icon, items, renderRow, emptyText, isAdmin, onAdd, onUnlink }) {
  return (
    <div className="obj-block">
      <div className="obj-block-header">
        {title}
        {isAdmin && onAdd && (
          <button className="obj-add-btn" onClick={onAdd} title="Associer un élément existant"><IcoPlus size={10} /></button>
        )}
      </div>
      {items.length === 0 ? (
        <EmptyRow icon={icon} text={emptyText} />
      ) : (
        <div>
          {items.slice(0, 8).map((item, i) => (
            <div key={item.id || i} className="obj-related-row">
              {renderRow(item)}
              {isAdmin && onUnlink && (
                <button className="obj-unlink-btn" title="Dissocier" onClick={(e) => { e.stopPropagation(); onUnlink(item); }}>✕</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Sélecteur « Associer un élément existant » (point 5) */
function LinkPicker({ title, items, renderLabel, renderMeta, onPick, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(it => `${renderLabel(it) || ""} ${renderMeta ? renderMeta(it) || "" : ""}`.toLowerCase().includes(s));
  }, [items, q, renderLabel, renderMeta]);
  return (
    <div className="camp-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="camp-panel obj-picker">
        <div className="camp-panel__header">
          <div className="camp-panel__title">{title}</div>
          <button className="camp-panel__close" onClick={onClose}>✕</button>
        </div>
        <div className="obj-picker-search">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…" autoFocus />
        </div>
        <div className="obj-picker-list">
          {filtered.length === 0 ? (
            <div className="obj-picker-empty">Aucun élément disponible à associer.</div>
          ) : filtered.map(it => (
            <button key={it.id} className="obj-picker-row" onClick={() => onPick(it)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="obj-rel-name">{renderLabel(it)}</div>
                {renderMeta && <div className="obj-rel-meta">{renderMeta(it)}</div>}
              </div>
              <span className="obj-picker-add"><IcoPlus size={11} /> Associer</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Création rapide de tâche depuis un objectif (point 5) */
function QuickTaskForm({ users, onSubmit, onClose }) {
  const [form, setForm] = useState({ title: "", deadline: "", priority: "Normale", assignedTo: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const userOptions = useMemo(
    () => (users || []).map(u => ({ id: String(u.id), name: userName(u) || u.email || "Membre" })),
    [users]
  );
  function toggle(id) {
    setForm(prev => {
      const cur = prev.assignedTo;
      return { ...prev, assignedTo: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  }
  async function handle() {
    if (!form.title.trim()) { setError("Le titre est obligatoire."); return; }
    setSaving(true); setError(null);
    try { await onSubmit(form); }
    catch (e) { console.error(e); setError("La tâche n'a pas pu être créée."); setSaving(false); }
  }
  return (
    <div className="camp-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="camp-panel">
        <div className="camp-panel__header">
          <div className="camp-panel__title">Nouvelle tâche liée à l'objectif</div>
          <button className="camp-panel__close" onClick={onClose} disabled={saving}>✕</button>
        </div>
        <div className="camp-panel__body">
          <div className="camp-form-group">
            <label className="camp-form-label">Titre <span className="camp-form-required">*</span></label>
            <input className="camp-form-input" type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} disabled={saving} autoFocus placeholder="Ex : Préparer le plan de publications" />
          </div>
          <div className="camp-form-row">
            <div className="camp-form-group">
              <label className="camp-form-label">Échéance</label>
              <input className="camp-form-input" type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} disabled={saving} />
            </div>
            <div className="camp-form-group">
              <label className="camp-form-label">Priorité</label>
              <select className="camp-form-input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} disabled={saving}>
                {["Basse", "Normale", "Haute", "Urgente"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="camp-form-group">
            <label className="camp-form-label">Assigné(s)</label>
            <div className="obj-check-grid">
              {userOptions.map(u => (
                <button key={u.id} type="button" className={`camp-channel-btn${form.assignedTo.includes(u.id) ? " active" : ""}`} onClick={() => toggle(u.id)} disabled={saving}>{u.name}</button>
              ))}
            </div>
          </div>
          {error && <div className="camp-form-error camp-form-error--block">{error}</div>}
        </div>
        <div className="camp-panel__footer">
          <button type="button" className="camp-btn camp-btn--ghost" onClick={onClose} disabled={saving}>Annuler</button>
          <button type="button" className="camp-btn camp-btn--primary" onClick={handle} disabled={saving || !form.title.trim()}>
            {saving ? <><Spinner /> Création…</> : "Créer la tâche"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRight({ obj, clubs, users, tasks, publications, events, campagnes, isAdmin, appId }) {
  const [picker, setPicker] = useState(null);        // "campagnes" | "events" | "publications" | "tasks"
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [ov, setOv] = useState({ campagnes: {}, events: {}, publications: {}, tasks: {} });
  const [newTasks, setNewTasks] = useState([]);

  // Tâches = serveur + créations locales (dédoublonné par id le temps du polling)
  const tasksAll = useMemo(() => {
    const m = new Map((tasks || []).map(t => [String(t.id), t]));
    newTasks.forEach(t => { if (!m.has(String(t.id))) m.set(String(t.id), t); });
    return Array.from(m.values());
  }, [tasks, newTasks]);

  const serverArr = { campagnes: campagnes || [], events: events || [], publications: publications || [], tasks: tasks || [] };
  const colls = { campagnes: campagnes || [], events: events || [], publications: publications || [], tasks: tasksAll };

  const effObj = (coll, item) => {
    const o = ov[coll];
    if (o && Object.prototype.hasOwnProperty.call(o, String(item.id))) return o[String(item.id)];
    return (item.objectiveId != null ? item.objectiveId : (item.objective != null ? item.objective : null));
  };
  const linkedOf = (coll) => colls[coll].filter(it => it && effObj(coll, it) != null && String(effObj(coll, it)) === String(obj.id));
  const candidatesOf = (coll) => colls[coll].filter(it => it && (effObj(coll, it) == null || effObj(coll, it) === ""));

  const linkedCampagnes = linkedOf("campagnes");
  const linkedEvents    = linkedOf("events");
  const linkedPubs      = linkedOf("publications");
  const linkedTasks     = linkedOf("tasks");

  const alerts = useMemo(() => computeAlerts(obj, linkedTasks), [obj, linkedTasks]);

  async function doLink(coll, item) {
    setOv(prev => ({ ...prev, [coll]: { ...prev[coll], [String(item.id)]: obj.id } }));
    setPicker(null);
    try { await setItemObjective(coll, serverArr[coll], item.id, obj.id); }
    catch (e) { console.error(e); setOv(prev => { const c = { ...prev[coll] }; delete c[String(item.id)]; return { ...prev, [coll]: c }; }); }
  }
  async function doUnlink(coll, item) {
    setOv(prev => ({ ...prev, [coll]: { ...prev[coll], [String(item.id)]: null } }));
    try { await setItemObjective(coll, serverArr[coll], item.id, null); }
    catch (e) { console.error(e); setOv(prev => { const c = { ...prev[coll] }; delete c[String(item.id)]; return { ...prev, [coll]: c }; }); }
  }
  async function handleCreateTask(form) {
    const id = await createTaskForObjective(tasks, form, obj.id, appId);
    setNewTasks(prev => [...prev, {
      id, title: form.title.trim(), status: "À faire",
      priority: form.priority || "Normale", deadline: form.deadline || "",
      assignedTo: form.assignedTo || [], objectiveId: obj.id,
    }]);
    setShowTaskForm(false);
  }

  const PICKERS = {
    campagnes:    { title: "Associer une campagne",  renderLabel: c => c.name || c.title, renderMeta: c => fmtShort(c.dateStart || c.date) },
    events:       { title: "Associer un événement",  renderLabel: e => e.title,           renderMeta: e => fmtShort(e.date) },
    publications: { title: "Associer un contenu",    renderLabel: p => p.title,           renderMeta: p => p.platform || "" },
    tasks:        { title: "Associer une tâche",     renderLabel: t => t.title,           renderMeta: t => t.deadline ? fmtShort(t.deadline) : "" },
  };

  return (
    <>
      {/* Alertes */}
      {alerts.length > 0 && (
        <div className="obj-block obj-block--alert">
          <div className="obj-block-header">
            <span style={{ color: "#8a5c00" }}><IcoAlert size={12} /></span>
            Alertes
            <span style={{ marginLeft: "auto", width: 18, height: 18, borderRadius: "50%", background: "var(--orange-bg)", color: "#8a5c00", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{alerts.length}</span>
          </div>
          {alerts.map((a, i) => (
            <div key={i} className={`obj-alert-row obj-alert-row--${a.type}`}>
              <IcoAlert size={11} /><span style={{ flex: 1 }}>{a.text}</span>
            </div>
          ))}
        </div>
      )}

      <RelatedBlock
        title="Campagnes liées" icon={<IcoMega size={15} />} emptyText="Aucune campagne liée"
        items={linkedCampagnes} isAdmin={isAdmin}
        onAdd={() => setPicker("campagnes")} onUnlink={(it) => doUnlink("campagnes", it)}
        renderRow={pr => (
          <>
            <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(0,113,205,.10)", color: "var(--ep-blue)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><IcoMega size={12} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="obj-rel-name">{pr.name || pr.title}</div>
              <div className="obj-rel-meta">{fmtShort(pr.dateStart || pr.date)}</div>
            </div>
          </>
        )}
      />

      <RelatedBlock
        title="Événements liés" icon={<IcoEvent size={15} />} emptyText="Aucun événement lié"
        items={linkedEvents} isAdmin={isAdmin}
        onAdd={() => setPicker("events")} onUnlink={(it) => doUnlink("events", it)}
        renderRow={ev => {
          const club = resolveClub(ev.club, clubs);
          return (
            <>
              <div className="obj-date-badge">
                <span>{fmtShort(ev.date).split(" ")[0]}</span>
                <span>{fmtShort(ev.date).split(" ")[1]}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="obj-rel-name">{ev.title}</div>
                <div className="obj-rel-meta">{club?.name}</div>
              </div>
            </>
          );
        }}
      />

      <RelatedBlock
        title="Contenus liés" icon={<IcoLayers size={15} />} emptyText="Aucun contenu lié"
        items={linkedPubs} isAdmin={isAdmin}
        onAdd={() => setPicker("publications")} onUnlink={(it) => doUnlink("publications", it)}
        renderRow={pub => {
          const s = (pub.status || pub.statut || "").toLowerCase();
          const isPub = s.includes("publi"); const isPend = s.includes("valider") || s.includes("review");
          const sp = isPub ? { bg: "var(--green-bg)", c: "var(--green)" } : isPend ? { bg: "var(--yellow-bg)", c: "#8a5c00" } : { bg: "var(--gray-bg)", c: "var(--text-3)" };
          return (
            <>
              <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(251,133,0,.10)", color: "#8a5c00", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><IcoLayers size={12} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="obj-rel-name">{pub.title}</div>
                <div className="obj-rel-meta" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  {pub.platform}
                  <Badge label={pub.status || "Brouillon"} bg={sp.bg} color={sp.c} />
                </div>
              </div>
            </>
          );
        }}
      />

      <RelatedBlock
        title="Tâches liées" icon={<IcoCheck size={15} />} emptyText="Aucune tâche liée"
        items={linkedTasks} isAdmin={isAdmin}
        onAdd={() => setPicker("tasks")} onUnlink={(it) => doUnlink("tasks", it)}
        renderRow={t => {
          const done = t.status === "Terminé" || t.status === "Terminée" || t.status === "done";
          return (
            <>
              <div style={{ width: 16, height: 16, borderRadius: 5, border: `1.5px solid ${done ? "var(--green)" : "var(--border-strong)"}`, background: done ? "var(--green)" : "transparent", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {done && <IcoCheck size={9} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="obj-rel-name" style={{ textDecoration: done ? "line-through" : "none", color: done ? "var(--text-3)" : "var(--text)" }}>{t.title}</div>
                {t.deadline && <div className="obj-rel-meta"><IcoCal size={10} /> {fmtShort(t.deadline)}</div>}
              </div>
            </>
          );
        }}
      />
      {isAdmin && (
        <button className="obj-create-task-btn" onClick={() => setShowTaskForm(true)}>
          <IcoPlus size={11} /> Créer une tâche pour cet objectif
        </button>
      )}

      {/* Historique */}
      <div className="obj-block">
        <div className="obj-block-header">Activité récente</div>
        <EmptyRow icon={<IcoClock size={15} />} text="L'activité apparaîtra ici au fil des mises à jour." />
      </div>

      {picker && (
        <LinkPicker
          title={PICKERS[picker].title}
          items={candidatesOf(picker)}
          renderLabel={PICKERS[picker].renderLabel}
          renderMeta={PICKERS[picker].renderMeta}
          onPick={(it) => doLink(picker, it)}
          onClose={() => setPicker(null)}
        />
      )}
      {showTaskForm && (
        <QuickTaskForm users={users} onSubmit={handleCreateTask} onClose={() => setShowTaskForm(false)} />
      )}
    </>
  );
}

/* ══════════════════════════════════════
   PAGE DÉTAIL
══════════════════════════════════════ */
function ObjectifDetail({ obj, clubs, users, tasks, publications, events, campagnes, appId, isAdmin, onEdit, onDuplicate, onArchive, onBack }) {
  const sc = statusColor(obj.status);
  const club = resolveClub(obj.club, clubs);
  const acc = clubAccentColor(club);

  return (
    <div className="obj-detail-page">
      {/* Breadcrumb */}
      <div className="obj-breadcrumb">
        <button className="obj-back-btn" onClick={onBack}><IcoChevL size={12} /> Objectifs</button>
        <IcoChevR size={10} />
        <span style={{ color: "var(--text)", fontWeight: 600, fontSize: 12 }}>{obj.title}</span>
      </div>

      {/* Header */}
      <div className="obj-detail-header">
        <div className="obj-detail-header-left">
          <div style={{ width: 48, height: 48, borderRadius: 15, background: `${acc}18`, color: acc, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <IcoTarget size={22} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
              <h1 className="obj-detail-title">{obj.title}</h1>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot, flexShrink: 0 }} />
              <Badge label={statusLabel(obj.status)} bg={sc.bg} color={sc.text} />
            </div>
            <p style={{ fontSize: 12, color: "var(--text-3)", margin: 0 }}>
              {club?.name} · {fmtDate(obj.startDate)} → {fmtDate(obj.deadline)}
            </p>
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {obj.archived && <Badge label="Archivé" bg="var(--gray-bg)" color="var(--text-3)" />}
            <button className="obj-btn-edit" onClick={onEdit}><IcoEdit size={13} /> Modifier</button>
            <MoreMenu onEdit={onEdit} onDuplicate={onDuplicate} onArchive={onArchive} archived={obj.archived} />
          </div>
        )}
      </div>

      {/* 3-col grid */}
      <div className="obj-detail-grid">
        <div className="obj-detail-col">
          <DetailLeft obj={obj} clubs={clubs} users={users} />
        </div>
        <div className="obj-detail-col">
          <DetailCenter obj={obj} users={users} tasks={tasks} />
        </div>
        <div className="obj-detail-col">
          <DetailRight obj={obj} clubs={clubs} users={users} tasks={tasks} publications={publications} events={events} campagnes={campagnes} appId={appId} isAdmin={isAdmin} />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   ÉDITEUR OBJECTIF (admin) — création / édition
══════════════════════════════════════ */
function Spinner() {
  return <span className="v2-spinner" style={{ width: 13, height: 13, borderWidth: 2, display: "inline-block", verticalAlign: "middle" }} />;
}

function ObjectiveEditor({ objective, objectives, clubs, users, appId, onClose, onSaved }) {
  const isEdit = !!objective;
  const [form, setForm] = useState(() => ({
    ...defaultObjective(),
    ...(objective || {}),
    // Normalise clubs en tableau pour l'UI
    clubs: Array.isArray(objective?.clubs)
      ? objective.clubs.map(String)
      : (objective?.club != null ? [String(objective.club)] : []),
    assignedTo: Array.isArray(objective?.assignedTo) ? objective.assignedTo.map(String) : [],
    owner: objective?.owner != null ? String(objective.owner) : String(appId || ""),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function setField(k, v) { setForm(prev => ({ ...prev, [k]: v })); }

  function onMetricChange(key) {
    const m = METRICS.find(x => x.key === key);
    setForm(prev => ({
      ...prev,
      metricKey: key,
      metricLabel: m && m.key !== "custom" ? m.label : prev.metricLabel,
      unit: m && m.key !== "custom" && m.unit ? m.unit : prev.unit,
    }));
  }

  function toggleClub(id) {
    setForm(prev => {
      const cur = prev.clubs || [];
      const s = String(id);
      return { ...prev, clubs: cur.includes(s) ? cur.filter(c => c !== s) : [...cur, s] };
    });
  }
  function toggleAssignee(id) {
    setForm(prev => {
      const cur = prev.assignedTo || [];
      const s = String(id);
      return { ...prev, assignedTo: cur.includes(s) ? cur.filter(c => c !== s) : [...cur, s] };
    });
  }

  const dateError = useMemo(() => {
    if (form.startDate && form.deadline && form.deadline < form.startDate)
      return "L'échéance ne peut pas être antérieure à la date de début.";
    return null;
  }, [form.startDate, form.deadline]);

  const targetError = useMemo(() => {
    if (form.target !== "" && form.baseline !== "" && Number(form.target) === Number(form.baseline))
      return "La cible doit être différente de la valeur de départ.";
    return null;
  }, [form.target, form.baseline]);

  const userOptions = useMemo(
    () => (users || []).map(u => ({ id: String(u.id), name: userName(u) || u.email || "Membre" })),
    [users]
  );

  async function handleSave() {
    if (!form.title.trim()) { setError("Le nom de l'objectif est obligatoire."); return; }
    if (dateError || targetError) return;
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      title: form.title.trim(),
      baseline: Number(form.baseline) || 0,
      target: Number(form.target) || 0,
      current: form.current === "" || form.current == null ? Number(form.baseline) || 0 : Number(form.current),
      // métrique personnalisée : pas de metricKey technique
      metricKey: form.metricKey === "custom" ? "" : form.metricKey,
    };
    try {
      let id;
      if (isEdit) { await updateObjective(objectives, objective.id, payload); id = objective.id; }
      else { id = await createObjective(objectives, payload, appId); }
      onSaved(id);
    } catch (err) {
      console.error(err);
      setError("Une erreur est survenue. L'objectif n'a pas été enregistré.");
      setSaving(false);
    }
  }

  const metricSelectValue = form.metricKey
    ? (METRICS.some(m => m.key === form.metricKey) ? form.metricKey : "custom")
    : "";

  return (
    <div className="camp-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="camp-panel camp-panel--large">
        <div className="camp-panel__header">
          <div className="camp-panel__title">{isEdit ? `Modifier — ${objective.title}` : "Nouvel objectif"}</div>
          <button className="camp-panel__close" onClick={onClose} disabled={saving}>✕</button>
        </div>

        <div className="camp-panel__body">
          {/* Général */}
          <div className="obj-form-section">Général</div>
          <div className="camp-form-group">
            <label className="camp-form-label">Nom de l'objectif <span className="camp-form-required">*</span></label>
            <input className="camp-form-input" type="text" value={form.title} onChange={e => setField("title", e.target.value)} disabled={saving} placeholder="Ex : Engagement moyen Instagram" autoFocus />
          </div>
          <div className="camp-form-group">
            <label className="camp-form-label">Résultat attendu</label>
            <textarea className="camp-form-input camp-form-textarea" value={form.description} onChange={e => setField("description", e.target.value)} disabled={saving} rows={2} placeholder="Décrire le résultat visé." />
          </div>

          {/* Périmètre */}
          <div className="obj-form-section">Périmètre</div>
          <div className="camp-form-group">
            <label className="camp-form-label">Périmètre</label>
            <div className="obj-seg">
              {[{ k: "france", l: "France" }, { k: "club", l: "Un club" }, { k: "clubs", l: "Plusieurs clubs" }].map(s => (
                <button key={s.k} type="button" className={`obj-seg-btn${form.scope === s.k ? " active" : ""}`} onClick={() => setField("scope", s.k)} disabled={saving}>{s.l}</button>
              ))}
            </div>
          </div>
          {form.scope === "club" && (
            <div className="camp-form-group">
              <label className="camp-form-label">Club</label>
              <select className="camp-form-input" value={form.clubs[0] || ""} onChange={e => setField("clubs", e.target.value ? [e.target.value] : [])} disabled={saving}>
                <option value="">— Choisir —</option>
                {(clubs || []).map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
            </div>
          )}
          {form.scope === "clubs" && (
            <div className="camp-form-group">
              <label className="camp-form-label">Clubs concernés</label>
              <div className="obj-check-grid">
                {(clubs || []).map(c => (
                  <button key={c.id} type="button" className={`camp-channel-btn${form.clubs.includes(String(c.id)) ? " active" : ""}`} onClick={() => toggleClub(c.id)} disabled={saving}>{c.name}</button>
                ))}
              </div>
            </div>
          )}

          {/* Indicateur */}
          <div className="obj-form-section">Indicateur</div>
          <div className="camp-form-row">
            <div className="camp-form-group">
              <label className="camp-form-label">Réseau social</label>
              <select className="camp-form-input" value={form.network} onChange={e => setField("network", e.target.value)} disabled={saving}>
                <option value="">Aucun (hors réseaux)</option>
                {NETWORKS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="camp-form-group">
              <label className="camp-form-label">Compte concerné</label>
              <input className="camp-form-input" type="text" value={form.account} onChange={e => setField("account", e.target.value)} disabled={saving} placeholder="@espritpadel…" />
            </div>
          </div>
          <div className="camp-form-row">
            <div className="camp-form-group">
              <label className="camp-form-label">Indicateur suivi</label>
              <select className="camp-form-input" value={metricSelectValue} onChange={e => onMetricChange(e.target.value)} disabled={saving}>
                <option value="">— Choisir —</option>
                {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div className="camp-form-group">
              <label className="camp-form-label">Unité</label>
              <input className="camp-form-input" type="text" value={form.unit} onChange={e => setField("unit", e.target.value)} disabled={saving} placeholder="%, abonnés…" />
            </div>
          </div>
          {metricSelectValue === "custom" && (
            <div className="camp-form-group">
              <label className="camp-form-label">Libellé de l'indicateur</label>
              <input className="camp-form-input" type="text" value={form.metricLabel} onChange={e => setField("metricLabel", e.target.value)} disabled={saving} placeholder="Ex : Réservations attribuées à la campagne" />
            </div>
          )}
          <div className="camp-form-group">
            <label className="camp-form-label">Sens de l'objectif</label>
            <div className="obj-seg">
              {[{ k: "increase", l: "Augmenter ↑" }, { k: "decrease", l: "Diminuer ↓" }].map(s => (
                <button key={s.k} type="button" className={`obj-seg-btn${form.direction === s.k ? " active" : ""}`} onClick={() => setField("direction", s.k)} disabled={saving}>{s.l}</button>
              ))}
            </div>
          </div>

          {/* Valeurs & échéances */}
          <div className="obj-form-section">Valeurs & échéances</div>
          <div className="camp-form-row">
            <div className="camp-form-group">
              <label className="camp-form-label">Valeur de départ</label>
              <input className="camp-form-input" type="number" step="any" value={form.baseline} onChange={e => setField("baseline", e.target.value)} disabled={saving} />
            </div>
            <div className="camp-form-group">
              <label className="camp-form-label">Date de référence</label>
              <input className="camp-form-input" type="date" value={form.baselineDate || ""} onChange={e => setField("baselineDate", e.target.value)} disabled={saving} />
            </div>
          </div>
          <div className="camp-form-row">
            <div className="camp-form-group">
              <label className="camp-form-label">Valeur actuelle</label>
              <input className="camp-form-input" type="number" step="any" value={form.current} onChange={e => setField("current", e.target.value)} disabled={saving} />
            </div>
            <div className="camp-form-group">
              <label className="camp-form-label">Valeur cible</label>
              <input className={`camp-form-input${targetError ? " camp-form-input--error" : ""}`} type="number" step="any" value={form.target} onChange={e => setField("target", e.target.value)} disabled={saving} />
            </div>
          </div>
          {targetError && <div className="camp-form-error">{targetError}</div>}
          <div className="camp-form-row">
            <div className="camp-form-group">
              <label className="camp-form-label">Date de début</label>
              <input className="camp-form-input" type="date" value={form.startDate || ""} onChange={e => setField("startDate", e.target.value)} disabled={saving} />
            </div>
            <div className="camp-form-group">
              <label className="camp-form-label">Échéance</label>
              <input className={`camp-form-input${dateError ? " camp-form-input--error" : ""}`} type="date" value={form.deadline || ""} onChange={e => setField("deadline", e.target.value)} disabled={saving} />
            </div>
          </div>
          {dateError && <div className="camp-form-error">{dateError}</div>}

          {/* Pilotage */}
          <div className="obj-form-section">Pilotage</div>
          <div className="camp-form-row">
            <div className="camp-form-group">
              <label className="camp-form-label">Responsable du pilotage</label>
              <select className="camp-form-input" value={form.owner || ""} onChange={e => setField("owner", e.target.value)} disabled={saving}>
                <option value="">— Choisir —</option>
                {userOptions.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="camp-form-group">
              <label className="camp-form-label">Priorité</label>
              <select className="camp-form-input" value={form.priority} onChange={e => setField("priority", e.target.value)} disabled={saving}>
                {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="camp-form-group">
            <label className="camp-form-label">Chargé(s) des actions</label>
            <div className="obj-check-grid">
              {userOptions.map(u => (
                <button key={u.id} type="button" className={`camp-channel-btn${form.assignedTo.includes(u.id) ? " active" : ""}`} onClick={() => toggleAssignee(u.id)} disabled={saving}>{u.name}</button>
              ))}
            </div>
          </div>
          <div className="camp-form-group">
            <label className="camp-form-label">Source des données</label>
            <select className="camp-form-input" value={form.source} onChange={e => setField("source", e.target.value)} disabled={saving}>
              {SOURCES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {form.source === "metricool" && (
              <span className="camp-form-hint">La synchronisation automatique n'est pas encore active : la valeur actuelle reste à saisir manuellement pour l'instant.</span>
            )}
          </div>

          {error && <div className="camp-form-error camp-form-error--block">{error}</div>}
        </div>

        <div className="camp-panel__footer">
          <button type="button" className="camp-btn camp-btn--ghost" onClick={onClose} disabled={saving}>Annuler</button>
          <button type="button" className="camp-btn camp-btn--primary" onClick={handleSave} disabled={saving || !form.title.trim() || !!dateError || !!targetError}>
            {saving ? <><Spinner /> Enregistrement…</> : isEdit ? "Enregistrer" : "Créer l'objectif"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Menu d'actions « ··· » (admin) */
function MoreMenu({ onEdit, onDuplicate, onArchive, archived }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="obj-btn-more" onClick={() => setOpen(o => !o)}>···</button>
      {open && (
        <div className="obj-more-menu">
          <button onClick={() => { setOpen(false); onEdit(); }}>Modifier</button>
          <button onClick={() => { setOpen(false); onDuplicate(); }}>Dupliquer</button>
          <button onClick={() => { setOpen(false); onArchive(); }}>{archived ? "Désarchiver" : "Archiver"}</button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   ROOT
══════════════════════════════════════ */
export default function ObjectifsV2({ appId, currentUser, onNavigate }) {
  const { objectives: serverObjectives, loading } = useObjectives();
  const { users } = useUsers();
  const { clubs } = useClubs();
  const { tasks } = useTasks();
  const { publications } = usePublications();
  const { events } = useCalendarEvents();
  const { campagnes } = useCampagnes();

  const isAdmin = currentUser?.admin === true;
  const syncStatus = useObjectivesSyncStatus();
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await fetch("/api/objectives/sync", { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error(`sync ${r.status}`);
      // Le polling onSnapshot (≤3s) remontera les nouvelles valeurs + le statut.
    } catch (e) {
      console.error("Actualisation objectifs KO:", e);
      window.alert("L'actualisation a échoué. Réessayez dans un instant.");
    } finally {
      setTimeout(() => setRefreshing(false), 1200);
    }
  }

  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing]       = useState(null);   // null | "new" | objet
  /* Overrides optimistes : reflètent une écriture avant que le polling (≤3s)
     ne la remonte. Purgés dès que le serveur a rattrapé (updatedAt). */
  const [overrides, setOverrides]   = useState({});

  const objectives = useMemo(() => {
    const byId = new Map((serverObjectives || []).map(o => [String(o.id), o]));
    Object.values(overrides).forEach(o => byId.set(String(o.id), o));
    return Array.from(byId.values());
  }, [serverObjectives, overrides]);

  // Purge des overrides rattrapés par le serveur
  useEffect(() => {
    setOverrides(prev => {
      if (!Object.keys(prev).length) return prev;
      let changed = false;
      const next = { ...prev };
      for (const o of serverObjectives || []) {
        const ov = next[String(o.id)];
        if (ov && Number(o.updatedAt || 0) >= Number(ov.updatedAt || 0)) {
          delete next[String(o.id)]; changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [serverObjectives]);

  function applyOverride(obj) {
    setOverrides(prev => ({ ...prev, [String(obj.id)]: obj }));
  }

  const visible   = useMemo(() => objectives.filter(o => !o.archived), [objectives]);
  const archived  = useMemo(() => objectives.filter(o => o.archived),  [objectives]);
  const selected  = selectedId != null ? objectives.find(o => String(o.id) === String(selectedId)) : null;

  async function handleDuplicate(obj) {
    const id = await duplicateObjective(objectives, obj.id, {}, appId);
    // Override optimiste : reflète la copie immédiatement (le serveur fait foi au prochain poll)
    applyOverride({
      ...obj, id, title: `${obj.title} (copie)`,
      status: "active", archived: false,
      current: Number(obj.baseline) || 0,
      createdAt: new Date().toISOString(), updatedAt: Date.now(),
    });
    setSelectedId(id);
  }

  async function handleArchive(obj) {
    const target = !obj.archived;
    if (target && !window.confirm(`Archiver « ${obj.title} » ? Il n'apparaîtra plus dans les vues actives (aucune suppression).`)) return;
    applyOverride({ ...obj, archived: target, updatedAt: Date.now() });
    await archiveObjective(objectives, obj.id, target);
    if (target) setSelectedId(null);
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: 60, color: "var(--text-3)", fontSize: 13 }}>
        <div className="v2-spinner" />
        <span>Chargement des objectifs…</span>
      </div>
    );
  }

  return (
    <>
      {editing && isAdmin && (
        <ObjectiveEditor
          objective={editing === "new" ? null : editing}
          objectives={objectives}
          clubs={clubs}
          users={users}
          appId={appId}
          onClose={() => setEditing(null)}
          onSaved={(id) => {
            setEditing(null);
            setSelectedId(id);
          }}
        />
      )}

      {selected ? (
        <ObjectifDetail
          obj={selected} clubs={clubs} users={users}
          tasks={tasks} publications={publications}
          events={events} campagnes={campagnes} appId={appId}
          isAdmin={isAdmin}
          onEdit={() => setEditing(selected)}
          onDuplicate={() => handleDuplicate(selected)}
          onArchive={() => handleArchive(selected)}
          onBack={() => setSelectedId(null)}
        />
      ) : (
        <ObjectifsList
          objectives={visible} archived={archived} clubs={clubs} users={users} tasks={tasks}
          isAdmin={isAdmin}
          syncStatus={syncStatus} refreshing={refreshing} onRefresh={handleRefresh}
          onSelect={(o) => setSelectedId(o.id)}
          onCreate={() => setEditing("new")}
        />
      )}
    </>
  );
}
